import {
    FC,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import type { ActivityResponse, Feed, FeedsClient } from '@stream-io/feeds-client'
import { StreamFeeds } from '@stream-io/feeds-client/react-bindings'

import { LoadingSpinner, ZeroState } from '@/components'
import { ReactionType } from '@/entities/Post/Post'
import { transformActivityToPost } from '@/entities/Post/PostTransformers'
import { trans } from '@/helpers/trans'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'

import { PostCard } from './subcomponents'

import './NewsfeedContainer.scss'

const HIGHLIGHT_DURATION_MS = 3000

interface NewsfeedContainerProps {
    client?: FeedsClient
    feed?: Feed
    activities: ActivityResponse[]
    loading: boolean
    hasConnectionError: boolean
    hasNextPage: boolean
    isLoadingNextPage: boolean
    onLoadNextPage: () => void
    unreadActivityIds: string[]
    onMarkAllRead: () => void
    focusActivityId?: string
}

export const NewsfeedContainer: FC<NewsfeedContainerProps> = ({
    client,
    feed,
    activities,
    loading,
    hasConnectionError,
    hasNextPage,
    isLoadingNextPage,
    onLoadNextPage,
    unreadActivityIds,
    onMarkAllRead,
    focusActivityId,
}): ReactElement => {
    const sentinelRef = useInfiniteScroll<HTMLLIElement>({
        hasNextPage,
        onLoadMore: onLoadNextPage,
    })

    const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())
    const [focusedActivity, setFocusedActivity] = useState<ActivityResponse>()
    const processedIdsRef = useRef<Set<string>>(new Set())
    const pendingTimeoutIdsRef = useRef<number[]>([])
    const resolvedFocusIdRef = useRef<string>(undefined)

    // Highlights the given (already unread) activity ids, then marks them as read. The removal
    // timeout is fire-and-forget (not effect cleanup) so it isn't cancelled by unrelated activity
    // updates (e.g. reactions) before the highlight has had its full 3s on screen.
    const highlightAndMarkRead = useCallback((ids: string[]): void => {
        if (! ids.length) {
            return
        }

        ids.forEach(id => processedIdsRef.current.add(id))
        setHighlightedIds(previous => {
            const next = new Set(previous)
            ids.forEach(id => next.add(id))
            return next
        })
        onMarkAllRead()

        const timeoutId = window.setTimeout(() => {
            setHighlightedIds(previous => {
                const next = new Set(previous)
                ids.forEach(id => next.delete(id))
                return next
            })
        }, HIGHLIGHT_DURATION_MS)

        pendingTimeoutIdsRef.current.push(timeoutId)
    }, [onMarkAllRead])

    // Highlight posts that were unread when they appeared in the currently loaded page.
    useEffect(() => {
        const newlyUnreadIds = activities
            .map(activity => activity.id)
            .filter(id => unreadActivityIds.includes(id) && ! processedIdsRef.current.has(id))

        highlightAndMarkRead(newlyUnreadIds)
    }, [activities, unreadActivityIds, highlightAndMarkRead])

    useEffect(() => () => {
        pendingTimeoutIdsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId))
    }, [])

    // Resolves the post a notification was clicked for: scrolls to it if it's already loaded,
    // otherwise fetches just that one activity and renders it above the feed (see the
    // "time gap" divider) instead of paging through history to find it.
    useEffect(() => {
        // Wait for the first page (e.g. when opened through a deep link), else every post seems not loaded yet
        if (! focusActivityId || loading || ! client || resolvedFocusIdRef.current === focusActivityId) {
            return
        }

        resolvedFocusIdRef.current = focusActivityId

        const existingElement = document.getElementById(`post-${focusActivityId}`)

        if (existingElement) {
            setFocusedActivity(undefined)
            // Always highlight the tapped notification's post, even if it's already read.
            highlightAndMarkRead([focusActivityId])
            existingElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
        }

        client.getActivity({ id: focusActivityId })
            .then(response => {
                setFocusedActivity(response.activity)
                highlightAndMarkRead([response.activity.id])
            })
            .catch(error => console.error(error))
    }, [focusActivityId, loading, activities, client, highlightAndMarkRead])

    const handleToggleReaction = async (activityId: string, type: ReactionType, isActive: boolean): Promise<void> => {
        if (! client) {
            return
        }

        try {
            if (isActive) {
                await client.deleteActivityReaction({ activity_id: activityId, type })
            } else {
                await client.addActivityReaction({ activity_id: activityId, type })
            }

            if (focusedActivity?.id === activityId) {
                const response = await client.getActivity({ id: activityId })

                setFocusedActivity(response.activity)
            }
        } catch (error) {
            console.error(error)
        }
    }

    if (hasConnectionError) {
        return (
            <div className="newsfeed">
                <ZeroState
                    text={trans('containers.newsfeedContainer.connectionError')}
                    className="newsfeed__zero-state"
                />
            </div>
        )
    }

    if (loading || ! client || ! feed) {
        return (
            <div className="newsfeed">
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <StreamFeeds client={client}>
            <div className="newsfeed">
                {focusedActivity && (
                    <>
                        <PostCard
                            post={transformActivityToPost(focusedActivity)}
                            activity={focusedActivity}
                            feed={feed}
                            client={client}
                            highlighted={highlightedIds.has(focusedActivity.id)}
                            onToggleReaction={handleToggleReaction}
                        />
                        <div className="newsfeed__time-gap" aria-hidden="true" />
                    </>
                )}

                {! activities.length ? (
                    <ZeroState
                        text={trans('containers.newsfeedContainer.noPostsFound')}
                        className="newsfeed__zero-state"
                    />
                ) : (
                    <ul className="newsfeed__list">
                        {activities.map(activity => (
                            <li key={activity.id} className="newsfeed__item">
                                <PostCard
                                    post={transformActivityToPost(activity)}
                                    activity={activity}
                                    feed={feed}
                                    client={client}
                                    highlighted={highlightedIds.has(activity.id)}
                                    onToggleReaction={handleToggleReaction}
                                />
                            </li>
                        ))}

                        {hasNextPage && (
                            <li ref={sentinelRef} className="newsfeed__sentinel">
                                {isLoadingNextPage && <LoadingSpinner />}
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </StreamFeeds>
    )
}
